import { Container } from "@/components/layout";
import { Text } from "@/components/ui/Text";
import * as React from "react";
import cn from "twsx";
import { PageTitle } from "./PageTitle";

// Every screen is a Page inside the AppShell panel. A page stacks fixed rows
// (PageHeader, then optional PageBar rows) above one PageBody. Rows are 44px,
// inset 16px on the left, and separated by border-subtle hairlines. List
// pages sit on the white panel like Linear's; other pages keep the canvas.

// `inset="profile"` widens the content inset to 32px from md.
export function Page({ children, surface = "canvas", inset = "default", className }: { children: React.ReactNode; surface?: "canvas" | "panel"; inset?: "default" | "profile"; className?: string }) {
  return (
    <div
      data-page={inset === "profile" ? "profile" : ""}
      className={cn(
        "flex min-h-0 w-full flex-1 flex-col overflow-hidden",
        surface === "canvas" ? "bg-bg-primary" : "[background-color:var(--shell-workspace-panel-bg)]",
        className,
      )}
    >
      {children}
    </div>
  );
}

// `layout="stack"` puts the actions on their own full-width row below the
// title on small screens, so wide action groups never squeeze the title.
export function PageHeader({ children, divider = true, layout = "inline", className }: { children: React.ReactNode; divider?: boolean; layout?: "inline" | "stack"; className?: string }) {
  return (
    <header
      data-page-header=""
      className={cn(
        "flex min-h-11 shrink-0 flex-wrap items-center justify-between gap-x-3 gap-y-1.5",
        "[background-color:var(--shell-workspace-panel-bg)] py-1.75 pr-2.5 pl-4",
        divider && "border-b border-border-secondary",
        layout === "stack" && "max-md:flex-col max-md:items-stretch max-md:[&>[data-page-actions]]:w-full",
        className,
      )}
    >
      {children}
    </header>
  );
}

// Header actions use Linear's 28px md control height.
export function PageActions({ children, className }: { children: React.ReactNode; className?: string }) {
  return <div data-page-actions="" className={cn("flex shrink-0 items-center gap-1.5", className)}>{children}</div>;
}

export function PageBar({
  children,
  actions,
  divider = false,
  className,
  wrap = true,
}: {
  children?: React.ReactNode;
  actions?: React.ReactNode;
  divider?: boolean;
  className?: string;
  wrap?: boolean;
}) {
  return (
    <div
      data-page-bar=""
      className={cn(
        "flex min-h-11 shrink-0 items-center justify-between gap-x-3 gap-y-1.5 py-1.5 pr-2.5 pl-4",
        "[background-color:var(--shell-workspace-panel-bg)]",
        divider && "border-b border-border-secondary",
        className,
      )}
    >
      {children && (
        <div className={cn("flex min-w-0 flex-1 items-center gap-1.5", wrap ? "flex-wrap" : "flex-nowrap overflow-hidden")}>
          {children}
        </div>
      )}
      {actions && <PageActions className={cn(!children && "ml-auto")}>{actions}</PageActions>}
    </div>
  );
}

// The page body. `padded` adds the page gutter; `maxed` centres a 1024px
// column. It scrolls both ways unless `scrollable` is false.
export function PageBody({
  children,
  scrollable = true,
  padded = false,
  maxed = false,
  viewportRef,
  className,
}: {
  children: React.ReactNode;
  scrollable?: boolean;
  padded?: boolean;
  maxed?: boolean;
  viewportRef?: React.Ref<HTMLDivElement>;
  className?: string;
}) {
  const gutter = "gap-8 py-5 pl-gutter-l pr-gutter-r md:px-8 md:py-6";
  return (
    <div
      ref={viewportRef}
      data-dashboard-body-scroll-viewport={scrollable ? "" : undefined}
      data-padded={padded}
      className={cn(
        "group/dashboard-screen-body relative flex min-h-0 w-full flex-1 flex-col",
        scrollable ? "iebridge-scrollbar overflow-auto" : "overflow-hidden",
        padded && !maxed && gutter,
        className,
      )}
    >
      {maxed ? <div className={cn("mx-auto flex w-full max-w-screen-lg flex-col items-start", padded && gutter)}>{children}</div> : children}
    </div>
  );
}

// ── ListPage ──────────────────────────────────────────────────────────────────
// Header (title, subtitle, primary actions) → optional view bar (tabs) →
// optional toolbar (search, filters, display controls) → full-bleed list body.
// The list owns its own scroll unless `scrollable` is set.

export type ListPageProps = {
  title: React.ReactNode;
  banner?: React.ReactNode;
  subtitle?: React.ReactNode;
  subtitleClassName?: string;
  actions?: React.ReactNode;
  tabs?: React.ReactNode;
  tools?: React.ReactNode;
  toolbarActions?: React.ReactNode;
  mobileToolsVariant?: "wrap" | "compact";
  headerSlot?: React.ReactNode;
  heroClassName?: string;
  headerLayout?: "inline" | "stack";
  scrollable?: boolean;
  surface?: "canvas" | "panel";
  children: React.ReactNode;
  className?: string;
};

export function ListPage({
  title,
  banner,
  subtitle,
  subtitleClassName,
  actions,
  tabs,
  tools,
  toolbarActions,
  mobileToolsVariant = "wrap",
  headerSlot,
  heroClassName,
  headerLayout,
  scrollable = false,
  surface = "panel",
  children,
  className,
}: ListPageProps) {
  return (
    <Page surface={surface} className={className}>
      {banner}
      <PageHeader layout={headerLayout} className={heroClassName}>
        <PageTitle subtitle={subtitle} subtitleClassName={subtitleClassName}>{title}</PageTitle>
        {actions && <PageActions>{actions}</PageActions>}
      </PageHeader>
      {tabs && <PageBar divider className="[&>div>*]:min-w-0 [&>div>*]:max-w-full">{tabs}</PageBar>}
      {(tools || toolbarActions) && (
        <PageBar divider actions={toolbarActions} wrap={mobileToolsVariant === "wrap"}>
          {tools}
        </PageBar>
      )}
      {headerSlot}
      <PageBody scrollable={scrollable}>{children}</PageBody>
    </Page>
  );
}

// ── StandardPage ──────────────────────────────────────────────────────────────
// Header with title and actions above a scrolling, full-bleed body. Use for
// simple lists, settings and detail pages that need no tabs or toolbar.

export type StandardPageProps = {
  title: React.ReactNode;
  actions?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
};

export function StandardPage({ title, actions, children, className }: StandardPageProps) {
  return (
    <Page className={className}>
      <PageHeader>
        <PageTitle>{title}</PageTitle>
        {actions && <PageActions>{actions}</PageActions>}
      </PageHeader>
      <PageBody>{children}</PageBody>
    </Page>
  );
}

// ── ProfilePage ───────────────────────────────────────────────────────────────
// Entity profile: breadcrumb header (when `title` is set) → identity row (hero + actions) → optional tab bar → body.
// `hero` owns its own avatar/name layout; `heroSlot` renders above it and
// `headerSlot` after the tab bar.

export type ProfilePageProps = {
  title?: React.ReactNode;
  hero: React.ReactNode;
  actions?: React.ReactNode;
  tabs?: React.ReactNode;
  heroSlot?: React.ReactNode;
  headerSlot?: React.ReactNode;
  heroDivider?: boolean;
  padded?: boolean;
  children: React.ReactNode;
  className?: string;
};

export function ProfilePage({
  title,
  hero,
  actions,
  tabs,
  heroSlot,
  headerSlot,
  heroDivider = false,
  padded = false,
  children,
  className,
}: ProfilePageProps) {
  return (
    <Page inset="profile" className={cn("[container:profile/inline-size]", className)}>
      {title && (
        <PageHeader>
          <PageTitle currentAs="span">{title}</PageTitle>
        </PageHeader>
      )}
      {heroSlot && <div className="shrink-0 [background-color:var(--shell-workspace-panel-bg)] px-5 pt-3 empty:hidden md:px-8">{heroSlot}</div>}
      <div
        className={cn(
          "dashboard-profile-hero flex shrink-0 flex-wrap items-center justify-between gap-x-4 gap-y-2",
          "[background-color:var(--shell-workspace-panel-bg)] px-5 py-4 md:px-8 md:py-5",
          heroDivider && "border-b border-border-secondary",
        )}
      >
        <div className="flex min-w-0 flex-1 items-center gap-4 max-md:gap-3">{hero}</div>
        {actions && <PageActions>{actions}</PageActions>}
      </div>
      {tabs && <PageBar divider className="pl-5 md:pl-8 [&>div>*]:min-w-0 [&>div>*]:max-w-full">{tabs}</PageBar>}
      {headerSlot}
      <PageBody padded={padded} scrollable={padded}>{children}</PageBody>
    </Page>
  );
}

// ── FormPage ──────────────────────────────────────────────────────────────────
// Create/edit flow: header with title and actions, then a centred form column
// (description, fields, footer). Width follows the layout Container scale.

export type FormPageProps = {
  title: React.ReactNode;
  description?: React.ReactNode;
  actions?: React.ReactNode;
  footer?: React.ReactNode;
  width?: "default" | "wide" | "full";
  density?: "default" | "compact";
  children: React.ReactNode;
  className?: string;
};

export function FormPage({ title, description, actions, footer, width = "default", density = "default", children, className }: FormPageProps) {
  const isCompact = density === "compact";

  return (
    <Page className={className}>
      <PageHeader>
        <PageTitle>{title}</PageTitle>
        {actions && <PageActions>{actions}</PageActions>}
      </PageHeader>
      <PageBody>
        <Container width={width} className={cn("flex flex-col px-5 md:px-6", isCompact ? "gap-6 py-6" : "gap-8 py-8")}>
          {description && <Text as="p" tone="tertiary" className="max-w-measure">{description}</Text>}
          <div className={cn("flex flex-col", isCompact ? "gap-6" : "gap-8")}>{children}</div>
          {footer && (
            <div className="flex flex-wrap items-center justify-end gap-2 border-t border-border-secondary pt-4">
              {footer}
            </div>
          )}
        </Container>
      </PageBody>
    </Page>
  );
}

// ── TabListSection ────────────────────────────────────────────────────────────
// Body of a tab whose content is its own list: a toolbar row (tools left,
// actions right) above a full-bleed list that owns its scroll. Render the list
// with `toolbarPlacement="external"` so it doesn't draw a second toolbar.

export type TabListSectionProps = {
  tools?: React.ReactNode;
  actions?: React.ReactNode;
  scrollable?: boolean;
  children: React.ReactNode;
  className?: string;
  toolbarClassName?: string;
};

export function TabListSection({ tools, actions, scrollable = false, children, className, toolbarClassName }: TabListSectionProps) {
  return (
    <PageBody scrollable={scrollable} className={className}>
      {(tools || actions) && (
        <PageBar divider actions={actions} className={toolbarClassName}>
          {tools}
        </PageBar>
      )}
      {children}
    </PageBody>
  );
}
